/**
 * Constants for the VoiceAgent component
 */

// API endpoints
export const ENDPOINTS = {
  LIVEKIT_TOKEN: '/api/v2/voice-agents/:agentId/livekit_tokens',
  AGENT_CONFIG: '/api/v2/voice-agents/:agentId/embed_config',
  CONVERSATION_EVENTS: '/api/v2/voice-agents/conversations/:conversationId/events',
} as const

// Connection settings
export const CONNECTION = {
  TOKEN_TTL: 15 * 60 * 1000, // 15 minutes in ms
  TOKEN_REFRESH_BUFFER: 60 * 1000, // Refresh 1 minute before expiry
  RECONNECT_ATTEMPTS: 3,
  RECONNECT_DELAY: 1000, // 1 second
  CONNECTION_TIMEOUT: 10000, // 10 seconds
} as const

// Audio settings
export const AUDIO = {
  DEFAULT_SAMPLE_RATE: 48000,
  DEFAULT_CHANNELS: 1,
  ECHO_CANCELLATION: true,
  NOISE_SUPPRESSION: true,
  AUTO_GAIN_CONTROL: true,
  LEVEL_UPDATE_INTERVAL: 100, // ms
  SILENCE_THRESHOLD: 0.01,
  SPEAKING_THRESHOLD: 0.15,
} as const

// UI settings
export const UI = {
  ANIMATION_DURATION: 300, // ms
  TRANSCRIPT_MAX_MESSAGES: 100,
  TRANSCRIPT_AUTO_SCROLL_THRESHOLD: 50, // px from bottom
  CHAT_INPUT_MAX_LENGTH: 500,
  POPUP_WIDTH: 380,
  POPUP_HEIGHT: 600,
  POPUP_MIN_HEIGHT: 60,
  INLINE_DEFAULT_HEIGHT: '600px',
  INLINE_DEFAULT_WIDTH: '100%',
} as const

// Visual settings
export const VISUALIZER = {
  BAR_COUNT: 5,
  BAR_WIDTH: 4,
  BAR_GAP: 4,
  MIN_HEIGHT: 20,
  MAX_HEIGHT: 40,
  ANIMATION_SPEED: 500, // ms
} as const

// Debug settings
export const DEBUG = {
  LOG_LEVEL: process.env.NODE_ENV === 'development' ? 'debug' : 'error',
  SHOW_LATENCY: true,
  SHOW_PACKET_LOSS: true,
  SHOW_CONNECTION_QUALITY: true,
  UPDATE_INTERVAL: 1000, // ms
} as const

// Error messages
export const ERRORS = {
  CONNECTION_FAILED: 'Failed to connect to voice agent',
  MICROPHONE_PERMISSION_DENIED: 'Microphone permission was denied',
  MICROPHONE_NOT_FOUND: 'No microphone found',
  TOKEN_EXPIRED: 'Session expired, please reconnect',
  AGENT_NOT_FOUND: 'Agent not found or not available',
  NETWORK_ERROR: 'Network error, please check your connection',
  UNKNOWN_ERROR: 'An unexpected error occurred',
} as const

// Success messages
export const MESSAGES = {
  CONNECTING: 'Connecting to agent...',
  CONNECTED: 'Connected',
  DISCONNECTED: 'Disconnected',
  RECONNECTING: 'Reconnecting...',
  MICROPHONE_ENABLED: 'Microphone enabled',
  MICROPHONE_DISABLED: 'Microphone disabled',
} as const

// Local storage keys
export const STORAGE_KEYS = {
  SELECTED_MICROPHONE: 'voiceAgent.selectedMicrophone',
  THEME_PREFERENCE: 'voiceAgent.theme',
  DEBUG_MODE: 'voiceAgent.debugMode',
  TRANSCRIPT_HISTORY: 'voiceAgent.transcriptHistory',
} as const

// CSS classes (for external styling)
export const CSS_CLASSES = {
  ROOT: 'voice-agent',
  POPUP: 'voice-agent-popup',
  INLINE: 'voice-agent-inline',
  MINIMIZED: 'voice-agent-minimized',
  CONNECTING: 'voice-agent-connecting',
  CONNECTED: 'voice-agent-connected',
  ERROR: 'voice-agent-error',
  DARK_THEME: 'voice-agent-dark',
  LIGHT_THEME: 'voice-agent-light',
} as const

// Agent states
export const AGENT_STATES = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  INITIALIZING: 'initializing',
  LISTENING: 'listening',
  THINKING: 'thinking',
  SPEAKING: 'speaking',
} as const

// Default configuration
export const DEFAULT_CONFIG = {
  authMode: 'internal',
  mode: 'popup',
  position: 'bottom-right',
  allowChat: true,
  showTranscript: true,
  debugMode: false,
  preConnectBuffer: false,
  theme: 'auto',
  autoConnect: true, // Auto-connect by default (set to false for embed mode)
  brandName: 'Resemble AI', // Brand name shown in idle state footer
} as const

// Keyboard shortcuts
export const KEYBOARD_SHORTCUTS = {
  TOGGLE_MICROPHONE: ' ', // Spacebar
  SEND_MESSAGE: 'Enter',
  CLOSE_POPUP: 'Escape',
  TOGGLE_DEBUG: 'ctrl+shift+d',
} as const

// Rate limiting
export const RATE_LIMITS = {
  CHAT_MESSAGES_PER_MINUTE: 30,
  TOKEN_REQUESTS_PER_MINUTE: 10,
  RECONNECT_ATTEMPTS_PER_MINUTE: 5,
} as const
